import { z } from 'zod';
import { AuditSchema } from './../settings/audit-schema';

export const EmployeeContractTypeSchema = z.enum([
  'indeterminado', // Fijo
  'determinado', // Temporal
  'obra', // Por proyecto
]);

export const EmployeeStatusSchema = z.enum([
  'active',
  'suspended',
  'terminated',
  'on_leave',
]);

export const ShiftTypeSchema = z.enum([
  'diurna', // 40h semanales max
  'nocturna', // 35h semanales max
  'mixta', // 37.5h semanales max
]);

export const NovedadTypeSchema = z.enum([
  'falta_justificada',
  'falta_injustificada',
  'permiso_pagado',
  'permiso_no_pagado',
  'reposo_medico',
  'suspension',
]);

export const PaymentMethodTypeSchema = z.enum([
  'transferencia', // Bank Transfer
  'pago_movil',    // Mobile Payment
  'efectivo_usd',  // Cash USD
  'efectivo_bs',   // Cash BS
]);

export const EmployeeSchema = z.object({
  businessId: z.string(),

  // Información Personal
  personalInfo: z.object({
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    idNumber: z.string().min(1), // Cédula en VE
    email: z.string().email().optional(),
    phone: z.string().optional(),
    address: z.string().optional(),
    birthDate: z.coerce.date().optional(),
  }),

  // Información Laboral
  jobInfo: z.object({
    position: z.string().min(1),
    department: z.string().optional(),
    hiringDate: z.coerce.date(),
    status: EmployeeStatusSchema.default('active'),
    contractType: EmployeeContractTypeSchema.default('indeterminado'),
    contractEndDate: z.coerce.date().optional(),
    contractDescription: z.string().optional(),
    shiftType: ShiftTypeSchema.default('diurna'),
  }),

  // Configuración Financiera (Nómina)
  payrollConfig: z.object({
    baseSalary: z.number().min(0),
    currency: z.enum(['USD', 'BS', 'EUR']).default('BS'),
    preferredPaymentMethod: PaymentMethodTypeSchema.default('efectivo_usd'),
    paymentFrequency: z
      .enum(['weekly', 'biweekly', 'monthly'])
      .default('biweekly'),

    // Configuración de Horas Extra
    overtimeConfig: z
      .object({
        dayRate: z.number().default(1.5), // +50%
        nightRate: z.number().default(2.0), // +100%
        holidayRate: z.number().default(2.5), // +150%
      })
      .default({}),

    // Deducciones de Ley (Venezuela)
    applyIVSS: z.boolean().default(true),
    applyFAOV: z.boolean().default(true),
    applyLRV: z.boolean().default(true), // Paro Forzoso

    // Otros
    cestaticketAmount: z.number().default(0), // Monto mensual
    islrPercentage: z.number().default(0), // Retención ISLR (Formulario ARI)
  }),

  // Información para el RNET (Ministerio del Trabajo)
  rnetInfo: z
    .object({
      hasDisability: z.boolean().default(false),
      ethnicity: z.string().default('No Aplica'),
      familyLoad: z.number().default(0),
      educationLevel: z.string().optional(),
    })
    .default({
      hasDisability: false,
      ethnicity: 'No Aplica',
      familyLoad: 0,
    }),

  // ... (keeping bankInfo, salaryHistory)
  bankInfo: z
    .object({
      bankName: z.string().optional(),
      bankCode: z.string().optional(),
      accountNumber: z
        .string()
        .regex(/^\d{20}$/, 'Debe tener exactamente 20 dígitos')
        .optional(),
      beneficiaryName: z.string().optional(),
      beneficiaryIdNumber: z.string().optional(),
      accountType: z.enum(['savings', 'checking']).optional(),
      pagoMovil: z
        .object({
          phone: z.string().optional(),
          idNumber: z.string().optional(),
          bankCode: z.string().optional(),
        })
        .optional(),
      utilityFraction: z.number().optional(),
      yearsOfService: z.number().optional(),
      totalDebt: z.number().optional(),
      pendingInstallments: z.number().optional(),
    })
    .optional(),

  salaryHistory: z
    .array(
      z.object({
        date: z.coerce.date(),
        amount: z.number(),
        reason: z.string().optional(),
        currency: z.enum(['USD', 'BS', 'EUR']).optional(),
      }),
    )
    .default([]),

  // Novedades del Periodo (Captura Pre-nómina - El Cache de Auditoría)
  novedades: z
    .array(
      z.object({
        id: z.string(),
        type: z.string(),
        label: z.string(),
        amount: z.number().default(0),
        currency: z.enum(['USD', 'BS', 'EUR']).default('USD'),
        quantity: z.number().default(1),
        unitLabel: z.string().optional(),
        description: z.string().optional(),
        date: z.coerce.date(),
        createdAt: z.coerce.date().default(() => new Date()),
        createdBy: z.object({
          uid: z.string(),
          name: z.string(),
          email: z.string().optional()
        }).optional(),
        impactsPayroll: z.boolean().default(true),
        status: z.enum(['pending', 'processed']).default('pending'),
        payrollCycleId: z.string().optional()
      }),
    )
    .default([]),

  // Pasivos Laborales Proyectados (Art. 142 LOTTT - El Doble Libro)
  accruedLiability: z
    .object({
      lastRecalculated: z.coerce.date(),

      // Libro A: Garantía + Adicionales (Art. 142a y 142b)
      severanceGarantia: z.number().default(0), // Acumulado de 15 días x trimestre
      daysAdditionalAccrued: z.number().default(0), // 2 días x año (Art. 142b)
      amountAdditionalAccrued: z.number().default(0), // Valor en $ de esos días adicionales
      severanceInterests: z.number().default(0), // Art. 143 (Intereses sobre garantía)
      accruedAdvances: z.number().default(0), // ART. 144: Adelantos pagados (Se restan del total)

      // Libro B: Retroactivo (Art. 142j)
      severanceRetro: z.number().default(0), // 30 días x año al último salario integral

      // Otros Pasivos
      vacationFraction: z.number().default(0),
      utilityFraction: z.number().default(0),
      pendingVacations: z.number().default(0), // Días de vacaciones vencidas de años anteriores

      // Snapshots de Cálculo
      integralSalaryDaily: z.number().default(0), // Salario Normal + Alicuotas
      normalSalaryDaily: z.number().default(0), // Solo Salario Base / 30
    })
    .optional(),

  metadata: z.record(z.string(), z.unknown()).optional(),

  audit: AuditSchema,
});

export type Employee = z.infer<typeof EmployeeSchema>;
export type EmployeeWithId = Employee & { id: string };
export type EmployeeCreate = Omit<Employee, 'audit'>;
